import type { ComboBoxOption } from 'components/inputs/types';
import { BULK_EDIT_KEEP_CURRENT } from '../constants';

export type SupplierUserBrandAccess = {
  accessToAllBrands?: boolean;
  assignedBrands?: Array<{ id: number }>;
};

/** Normalize API customProps without type assertions. */
function parseSupplierUserBrandAccess(customProps: unknown): SupplierUserBrandAccess {
  if (customProps == null || typeof customProps !== 'object') {
    return { assignedBrands: [] };
  }
  const accessToAllBrands = Reflect.get(customProps, 'accessToAllBrands') === true;
  const brandsRaw = Reflect.get(customProps, 'assignedBrands');
  const assignedBrands: Array<{ id: number }> = [];
  if (Array.isArray(brandsRaw)) {
    for (const item of brandsRaw) {
      if (item != null && typeof item === 'object') {
        const id = Number(Reflect.get(item, 'id'));
        if (!Number.isNaN(id)) assignedBrands.push({ id });
      }
    }
  }
  return { accessToAllBrands, assignedBrands };
}

/** True if the supplier contact option may work with the given brand id. */
export function supplierContactOptionHasBrandAccess(
  option: ComboBoxOption,
  brandId: number
): boolean {
  if (option.id === '__clear__') return true;
  const props = parseSupplierUserBrandAccess(option.customProps);
  if (props.accessToAllBrands) return true;
  return (props.assignedBrands ?? []).some(b => b.id === brandId);
}

/** True if the brand option is allowed for the given supplier contact selection. */
export function brandOptionAllowedForSupplierContact(
  option: ComboBoxOption,
  supplierContact: unknown
): boolean {
  if (option.id === '__clear__') return true;
  if (
    supplierContact == null ||
    supplierContact === BULK_EDIT_KEEP_CURRENT ||
    typeof supplierContact !== 'object'
  ) {
    return true;
  }
  if (!('customProps' in supplierContact)) {
    return true;
  }
  const rawCp = Reflect.get(supplierContact, 'customProps');
  if (rawCp == null) {
    return true;
  }
  const props = parseSupplierUserBrandAccess(rawCp);
  if (props.accessToAllBrands) return true;
  const bid = Number(option.id);
  if (Number.isNaN(bid)) return true;
  return (props.assignedBrands ?? []).some(b => b.id === bid);
}
